import React, { memo, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface DashboardHeaderProps {
  lastUpdated: Date | null;
  isEmergency: boolean;
  onSettingsClick: () => void;
}

export const DashboardHeader = memo(function DashboardHeader({ lastUpdated, isEmergency, onSettingsClick }: DashboardHeaderProps) {
  const formatTime = (date: Date | null) => {
    if (!date) return '--:--:--';
    return date.toLocaleTimeString();
  };

  return (
    <header className="bg-dashboard-card border-b border-dashboard-border">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
        <div className="flex items-center justify-between gap-2">
          {/* Logo & Title */}
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-solar to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-bold text-white truncate">Solar Controller</h1>
              <p className="text-xs text-slate-400 hidden sm:block">Power Management System</p>
            </div>
          </div>

          {/* Status & Controls */}
          <div className="flex items-center gap-2 sm:gap-4 md:gap-6 flex-shrink-0">
            {/* System Status - compact on mobile */}
            <div className="flex items-center space-x-1 sm:space-x-2">
              <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${isEmergency ? 'bg-red-500 animate-pulse' : 'bg-green-500'}`}></div>
              <span className={`text-xs sm:text-sm font-medium ${isEmergency ? 'text-red-400' : 'text-green-400'}`}>
                {isEmergency ? 'Emergency' : 'OK'}
              </span>
            </div>

            {/* Last Updated - hidden on small mobile */}
            <div className="hidden sm:block text-sm text-slate-400">
              <span className="hidden md:inline text-slate-500">Updated: </span>
              <span className="font-mono">{formatTime(lastUpdated)}</span>
            </div>

            {/* Advanced Devices Link */}
            <Link
              to="/devices"
              className="p-1.5 sm:p-2 rounded-lg bg-dashboard-card-hover hover:bg-slate-600 transition-colors"
              title="Advanced Device View"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
            </Link>

            {/* Settings Button */}
            <button
              onClick={onSettingsClick}
              className="p-1.5 sm:p-2 rounded-lg bg-dashboard-card-hover hover:bg-slate-600 transition-colors"
              title="Settings"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
});

export default DashboardHeader;
